/**
 * @file Sortable系コンポーネントの共通ユーティリティ
 */

import type { DragEndEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import type { BaseProject, ProjectGroupType } from "./types";
import { PROJECT_TYPE_TO_PATH } from "./types";

/**
 * プロジェクトのパスパターンを生成
 */
function createProjectPathPatterns(
  workspaceId: string,
  projectId: string,
  projectType: ProjectGroupType,
): string[] {
  if (projectType === "dashboards") {
    const basePath = `/${encodeURIComponent(workspaceId)}/dashboards/${encodeURIComponent(projectId)}`;
    return [basePath];
  }

  const pathSegment = PROJECT_TYPE_TO_PATH[projectType];
  const basePath = `/${encodeURIComponent(workspaceId)}/${pathSegment}/${encodeURIComponent(projectId)}`;
  return [
    `${basePath}/(board|list)$`,
    `${basePath}/settings$`,
    `${basePath}/[^/]+/?$`, // ビューのパス（プロジェクトID以降に任意のパスセグメントが続く）
  ];
}

/**
 * プロジェクトがアクティブかどうかを判定
 */
function isProjectActive(
  currentPathname: string,
  workspaceId: string,
  projectId: string,
  projectType: ProjectGroupType,
): boolean {
  const patterns = createProjectPathPatterns(
    workspaceId,
    projectId,
    projectType,
  );
  return patterns.some((pattern) =>
    new RegExp(`^.*${pattern}`).test(currentPathname),
  );
}

/**
 * プロジェクトにアクティブ状態を追加
 */
export function addActiveStateToProjects<T extends BaseProject>(
  projects: T[],
  currentPathname: string,
  workspaceId: string,
  projectType: ProjectGroupType,
): (T & { isProjectActive: boolean })[] {
  return projects.map((project) => ({
    ...project,
    isProjectActive: isProjectActive(
      currentPathname,
      workspaceId,
      project.id,
      projectType,
    ),
  }));
}

/**
 * ドラッグ終了時の共通処理
 */
export function handleDragEndCommon<T extends { id: string }>(
  event: DragEndEvent,
  items: T[],
): { reorderedItems: T[]; reorderedIds: string[] } | null {
  const { active, over } = event;

  if (!over || active.id === over.id) {
    return null;
  }

  const oldIndex = items.findIndex((item) => item.id === active.id);
  const newIndex = items.findIndex((item) => item.id === over.id);

  if (oldIndex === -1 || newIndex === -1) {
    return null;
  }

  const reorderedItems = arrayMove(items, oldIndex, newIndex);
  const reorderedIds = reorderedItems.map((item) => item.id);

  return { reorderedItems, reorderedIds };
}

/**
 * プロジェクトタイプの変換マップ
 */
export const PROJECT_TYPE_MAP = {
  IW: "INDEX_WORKER",
  SA: "SEARCH_ANALYTICS",
  UA: "USER_ANALYTICS",
  ADS: "ADS_ANALYTICS",
  DASHBOARD: "DASHBOARD",
} as const;

/**
 * プロジェクトタイプからAPIタイプへの変換
 */
export function getApiType(projectType: keyof typeof PROJECT_TYPE_MAP): string {
  return PROJECT_TYPE_MAP[projectType];
}

/**
 * 順序配列による並び替え処理（es-toolkit風の実装）
 */
export function reorderByIds<T extends { id: string }>(
  items: T[],
  orderIds?: string[],
): T[] {
  if (!orderIds?.length) return items;

  // IDをキーとしたMapを作成（O(n)）
  const itemMap = new Map<string, T>(items.map((item) => [item.id, item]));

  // 順序に従って配列を構築
  const ordered: T[] = [];
  const usedIds = new Set<string>();

  for (const id of orderIds) {
    const item = itemMap.get(id);
    if (item) {
      ordered.push(item);
      usedIds.add(id);
    }
  }

  // 順序配列にない新しいアイテムを追加
  for (const item of items) {
    if (!usedIds.has(item.id)) {
      ordered.push(item);
    }
  }

  return ordered;
}
